import { AnalyticsStatistic } from '@components/charts/analytics/Statistic'
import PlanRepartition from '@components/charts/membership/analytics-overview/PlanRepartition'
import {
  figureValue,
  formatDecimal,
  formatInteger,
  formatPercent,
  lines,
} from '@components/charts/membership/revenue/figures'
import useRevenueStatistics from '@components/charts/membership/revenue/useRevenueStatistics'
import { ResponseMembershipClubTodayAnalyticsStatistics } from '@models/membership/MembershipAnalyticsStatistics'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { Col, Flex, Row, Typography } from 'antd'

const { Text } = Typography

/** Snapshot of today (no section title): KPI tiles + plan repartition of the active base. */
const ClubToday = () => {
  const { data, isLoading, error } =
    useRevenueStatistics<ResponseMembershipClubTodayAnalyticsStatistics>(
      'membership-club-today',
      false
    )

  const money = (cents?: number | null, showCents = false) =>
    analyticsCurrency(cents, showCents, data?.currency)

  const activeMembers = figureValue(data?.activeMembers)
  const expectedYears = figureValue(data?.expectedYears)
  const averageYears = figureValue(data?.averageYearsPaid)
  const ltv = figureValue(data?.averageLtv)
  const toCancelRate = figureValue(data?.toCancelRate)
  const toCancelArr = figureValue(data?.toCancelArr)

  return (
    <Flex vertical gap={24}>
      <Row gutter={[24, 24]} align="stretch">
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Active subscriptions"
            tooltip="Subscriptions that are currently active. A Couple plan counts once here and twice in members."
            value={formatInteger(figureValue(data?.activeSubscriptions))}
            secondary={
              activeMembers !== null
                ? `${formatInteger(activeMembers)} members`
                : undefined
            }
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="ARR"
            tooltip="Annual recurring revenue: what the active subscriptions will bring at their next renewal, after coupons."
            value={data && money(figureValue(data.arr))}
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Revenue per subscription"
            tooltip="ARR divided by active subscriptions."
            value={
              data && money(figureValue(data.revenuePerSubscription), true)
            }
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Average membership length"
            tooltip="How many years of fees a subscription has paid on average since the program launched. It can only grow as the program gets older."
            value={
              averageYears !== null
                ? `${formatDecimal(averageYears)} years paid`
                : undefined
            }
            secondary={lines(
              ltv !== null &&
                `Lifetime value ${money(ltv)}${
                  expectedYears !== null
                    ? ` · expected ${formatDecimal(expectedYears)} years at current churn`
                    : ''
                }`
            )}
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Cancelling at renewal"
            tooltip="Members who asked to cancel. They keep access until their renewal date and will not be charged."
            value={formatInteger(figureValue(data?.toCancel))}
            secondary={lines(
              toCancelRate !== null &&
                `${formatPercent(toCancelRate)} of active subscriptions`,
              toCancelArr !== null && `${money(toCancelArr)} of ARR at risk`
            )}
            isLoading={isLoading}
          />
        </Col>
      </Row>
      {error && <Text type="danger">{error}</Text>}
      <PlanRepartition />
    </Flex>
  )
}

export default ClubToday
